import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Row, Spinner, Badge, Card, Col, Modal } from 'react-bootstrap';
import toast, { Toaster } from 'react-hot-toast';
import ReactPaginate from 'react-paginate';
import { AiOutlineEye } from "react-icons/ai";
import { RxDashboard } from "react-icons/rx";
import { BsSkipEndCircle } from "react-icons/bs";
import { GrFormNext, GrFormPrevious } from "react-icons/gr";
import moment from 'moment'
import CryptoJS from 'crypto-js';
import styled from 'styled-components';
import 'bootstrap-icons/font/bootstrap-icons.css';

// Import header image
import AssessmentHeaderImage from '../../../assets/images/assessment-header.jpg';
import Logo from '../../../assets/images/logo.png';

// Custom Component
import NavbarComponent from "../../components/layouts/Navbar";
import UploadedDocumentsModal from "../../components/modal/UploadedDocumentsModal";
import ShowPdfModal from "../../components/modal/ShowPdfModal";
import UserJourneyModal from "../../components/modal/UserJourneyModal";
import Footer from '../../components/layouts/Footer'

// Utility-Service
import { getToken, getUser } from "../../../utility/AuthService";
import UserJourneyManager, { UserJourneyStates } from "../../../utility/userJourneyManager";

//Redux-Service
import { selectLanguageUse } from '../../../redux/slices/language.Slice';

// API-Service
import { startSubmissionService, startProgressSubmissionService, getAssesmentListService, getProgressAssesmentListService } from '../../../services/submissionService';
import { getActiveDimensionListService, getActiveProgressDimensionListService } from "../../../services/dimensionService";

const StyledHistory = styled.div`
    .header-image {
        width: 100%;
        height: 200px;
        margin-bottom: 2rem;
        border-radius: 12px;
        overflow: hidden;
        
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    .assessment-cards {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 1.5rem;
        padding: 1.5rem;
    }

    .assessment-card {
        background: white;
        border-radius: 12px;
        padding: 1.5rem;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        cursor: pointer;

        &:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 12px rgba(0, 0, 0, 0.15);
        }

        .card-header {
            display: flex;
            align-items: center;
            margin-bottom: 1rem;

            i {
                font-size: 1.5rem;
                margin-right: 1rem;
                color: #4a90e2;
            }

            h3 {
                color: #ffffff;
                margin: 0;
            }
        }

        .card-content {
            color: #666;
            font-size: 0.95rem;
            line-height: 1.5;
        }

        .card-footer {
            margin-top: 1rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            
            .date {
                color: #999;
                font-size: 0.9rem;
            }

            .status {
                padding: 0.25rem 0.75rem;
                border-radius: 12px;
                font-size: 0.85rem;
                font-weight: 500;

                &.completed {
                    background: #e8f5e9;
                    color: #2e7d32;
                }

                &.in-progress {
                    background: #fff3e0;
                    color: #f57c00;
                }
            }
        }
    }

    .selection-section {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        padding: 2rem;
        border-radius: 12px;
        margin: 1.5rem;
        color: #495057;

        h2 {
            margin-bottom: 1.5rem;
            color: #2c3e50;
        }

        .assessment-types {
            display: flex;
            gap: 1rem;
            flex-wrap: wrap;
        }

        .assessment-type {
            background: rgba(255, 255, 255, 0.8);
            padding: 1rem;
            border-radius: 8px;
            cursor: pointer;
            transition: background 0.2s ease;
            display: flex;
            align-items: center;
            border: 1px solid #dee2e6;

            i {
                margin-right: 0.5rem;
                font-size: 1.25rem;
                color: #4a90e2;
            }

            &:hover {
                background: rgba(255, 255, 255, 1);
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            }

            &.active {
                background: #fff;
                border-color: #4a90e2;
            }
        }
    }

    padding: 2rem;

    .assessment-type {
        margin-bottom: 3rem;
    }

    .assessment-card {
        background: #ffffff;
        border: none;
        border-radius: 12px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
        height: 100%;
        
        &:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
        }

        &.disabled {
            opacity: 0.7;
            cursor: not-allowed;

            .card-header {
                background: #6c757d;
            }
        }
    }

    .card-header {
        background: linear-gradient(135deg, #2c5282 0%, #3182ce 100%);
        color: white;
        border-radius: 12px 12px 0 0;
        padding: 1.5rem;
        font-size: 1.25rem;
        font-weight: 600;
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);

        .info-icon {
            color: white;
            font-size: 1.2rem;
            cursor: pointer;
            transition: opacity 0.2s ease;

            &:hover {
                opacity: 0.8;
            }
        }
    }

    .card-body {
        padding: 1.5rem;
    }

    .assessment-info {
        margin-bottom: 1rem;
        color: #1e3250;
    }

    .step-number {
        background: #1e3250;
        color: white;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 1rem;
        font-weight: 600;
    }

    .report-section {
        margin-top: 3rem;
        padding: 2rem;
        background: #f8f9fa;
        border-radius: 12px;
        border: 1px solid #e0e0e0;
    }

    .report-card {
        background: white;
        border-radius: 8px;
        padding: 1.5rem;
        margin-bottom: 1rem;
        border: 1px solid #e0e0e0;
        transition: all 0.3s ease;

        &:hover {
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        &.disabled {
            opacity: 0.7;
            cursor: not-allowed;

            h5 {
                color: #6c757d;
            }
        }
    }

    .btn-primary {
        background: #1e3250;
        border: none;
        color: white;
        
        &:hover:not(:disabled) {
            background: #2c4a74;
        }
        
        &:disabled {
            background: #6c757d;
            opacity: 0.7;
        }
    }

    .btn-outline-primary {
        border-color: #1e3250;
        color: #1e3250;
        
        &:hover:not(:disabled) {
            background: #1e3250;
            color: white;
        }
        
        &:disabled {
            border-color: #6c757d;
            color: #6c757d;
        }
    }

    .modal-header {
        background: #1e3250;
        color: white;
        border-radius: 12px 12px 0 0;

        .btn-close {
            filter: brightness(0) invert(1);
        }
    }

    .guides-grid {
        transition: all 0.3s ease-in-out;
    }

    .guide-card {
        animation: fadeIn 0.3s ease-in-out;
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .guides-section {
        padding: 2rem 0;
        background: #f8f9fa;
        border-radius: 12px;
        margin: 2rem 0;
        transition: all 0.3s ease;
    }

    .guides-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 1rem;
    }

    .guides-wrapper {
        display: flex;
        gap: 1.5rem;
        margin-bottom: 2rem;
        transition: transform 0.5s ease;
    }

    .guide-card {
        flex: 1;
        background: white;
        padding: 2rem;
        border-radius: 12px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
        text-align: center;
        min-height: 250px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .guide-card:hover {
        transform: translateY(-5px);
        box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    }

    .guide-icon {
        width: 64px;
        height: 64px;
        background: #e3f2fd;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1rem;
    }

    .guide-icon i {
        font-size: 1.5rem;
        color: #1976d2;
    }

    .guide-card h5 {
        margin: 1rem 0;
        color: #333;
        font-size: 1.1rem;
    }

    .guide-card p {
        color: #666;
        font-size: 0.95rem;
        line-height: 1.5;
        margin: 0;
    }

    .guides-navigation {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1rem;
    }

    .nav-arrow {
        background: white;
        border: 1px solid #ddd;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
    }

    .nav-arrow:hover {
        background: #f0f0f0;
        border-color: #1976d2;
        color: #1976d2;
    }

    .nav-bullets {
        display: flex;
        gap: 0.5rem;
    }

    .nav-bullet {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #ddd;
        border: none;
        cursor: pointer;
        transition: all 0.3s ease;
        padding: 0;
    }

    .nav-bullet.active {
        background: #1976d2;
        transform: scale(1.2);
    }

    .welcome-content {
        text-align: center;
        padding: 1rem;
    }

    .welcome-steps {
        text-align: left;
    }

    .step-number {
        width: 32px;
        height: 32px;
        background: #4CAF50;
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        flex-shrink: 0;
    }

    .step h5 {
        color: #333;
        font-size: 1.1rem;
        margin-bottom: 0.5rem;
    }

    .step p {
        color: #666;
        font-size: 0.95rem;
        line-height: 1.5;
    }
`;

const History = () => {
    const Separation_Url = process.env.REACT_APP_SEPARATION
    const [isLoading, setLoading] = useState(false);
    const [assesmentLoading, setAssesmentLoading] = useState(false);
    const [selectedType, setSelectedType] = useState('regular');
    const [assesmentList, setAssesmentList] = useState([]);
    const [progressAssesmentList, setProgressAssesmentList] = useState([]);
    const [dimensionList, setDimensionList] = useState([])
    const [progressDimensionList, setProgressDimensionList] = useState([])
    const [totalEntry, setTotalEntry] = useState()
    const [totalProgressEntry, setTotalProgressEntry] = useState()
    const [submissionId, setSubmissionId] = useState()
    const authToken = getToken()
    const user = getUser()
    const navigate = useNavigate()
    const dispatch = useDispatch();
    const languageUse = useSelector(selectLanguageUse || user?.language)

    const [uploadedDocumentsModal, setUploadedDocumentsModal] = useState(false);
    const [progress, setProgress] = useState(false);
    const [pdfItem, setPdfItem] = useState();
    const [showPdfModalShow, setShowPdfModalShow] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [modalContent, setModalContent] = useState({});
    const [assessmentStatus, setAssessmentStatus] = useState({
        regular: false,
        progress: false
    });
    // Remove showWelcomeModal state

    // User Journey Management
    const [userJourneyState, setUserJourneyState] = useState(UserJourneyStates.FIRST_TIME);
    const [showUserJourneyModal, setShowUserJourneyModal] = useState(false);
    const journeyManager = new UserJourneyManager();

    const handleShowPdfModalShow = () => setShowPdfModalShow(!showPdfModalShow);

    const UploadedDocumentsModaltoggle = (id, text) => {
        if (text === 'progressAssessment') {
            setProgress(true)
        } else {
            setProgress(false)
        }
        setSubmissionId(id)
        setUploadedDocumentsModal(!uploadedDocumentsModal)
    };

    const [currentPage, setCurrentPage] = useState(0)
    const [progressCurrentPage, setProgressCurrentPage] = useState(0)
    const [offsetCal, setoffsetCal] = useState(0)
    const [progressOffsetCal, setProgressOffsetCal] = useState(0)

    const pageLimit = 10

    /**
        * @function getDimensionList
        * @param
        * @description to get Dimension List
    */
    const getDimensionList = async () => {
        setLoading(true)
        try {
            const result = await getActiveDimensionListService({
                authToken,
            })
            if (result?.data?.statusCode === 200) {
                setDimensionList(result?.data?.data?.list)
                setLoading(false)
            } else {
                setLoading(false)
                setDimensionList([])
                toast.error(result.data.errorMessage || 'Something went wrong!')
            }

        } catch (error) {
            setLoading(false)
            setDimensionList([])
            toast.error(error?.response?.data?.message || 'Something went wrong!')
        }
    };

    /**
        * @function getProgressDimensionList
        * @param
        * @description to get Progress Dimension List
    */
    const getProgressDimensionList = async () => {
        setLoading(true)
        try {
            const result = await getActiveProgressDimensionListService({
                authToken,
            })
            if (result?.data?.statusCode === 200) {
                setProgressDimensionList(result?.data?.data?.list)
                setLoading(false)
            } else {
                setLoading(false)
                setProgressDimensionList([])
                toast.error(result.data.errorMessage || 'Something went wrong!')
            }
        } catch (error) {
            setLoading(false)
            setProgressDimensionList([])
            toast.error(error?.response?.data?.message || 'Something went wrong!')
        }
    };

    useEffect(() => {
        getDimensionList()
        getProgressDimensionList()
    }, []);

    /**
        * @function goToDashboard
        * @param S_id
        * @description navigate to Dashboard page
    */
    const goToDashboard = (S_id) => {
        const encryptedSubId = CryptoJS.AES.encrypt(S_id, 'mySecretKey').toString();
        navigate(`/user/dashboard/${encodeURIComponent(encryptedSubId)}`)
    };

    /**
        * @function goToProgressDashboard
        * @param S_id
        * @description navigate to Dashboard page
    */
    const goToProgressDashboard = (S_id) => {
        const encryptedSubId = CryptoJS.AES.encrypt(S_id, 'mySecretKey').toString();
        navigate(`/user/progress/dashboard/${encodeURIComponent(encryptedSubId)}`)
    };

    /**
        * @function goToAssociatedCompanies
        * @param
        * @description navigate to associated companines page
    */
    const goToAssociatedCompanies = () => {
        navigate('/associatedCompanies')
    };

    /**
        * @function goToProgressAssociatedCompanies
        * @param
        * @description navigate to progress associated companines page
    */
    const goToProgressAssociatedCompanies = () => {
        navigate('/progress/associatedCompanies')
    };

    /**
        * @function handleEdit
        * @param status, S_id, text
        * @description use encrypted submission id to go to the question page.
    */
    const handleEdit = (status, S_id, text) => {
        const encryptedSubId = CryptoJS.AES.encrypt(S_id, 'mySecretKey').toString();
        if (text === 'progressAssessment') {
            if (status) {
                navigate(`/progress/${encodeURIComponent(encryptedSubId)}`)
            } else {
                navigate(`/view/progress/${encodeURIComponent(encryptedSubId)}`)
            }
        } else {
            if (status) {
                navigate(`/${encodeURIComponent(encryptedSubId)}`)
            } else {
                navigate(`/view/${encodeURIComponent(encryptedSubId)}`)
            }
        }
    }

    /**
        * @function getDocumentsView
        * @param status, id, text
        * @description use condition to return desired button.
    */
    const getDocumentsView = (status, id, text) => {
        if (!status) {
            return <Button className="login-btn-min-width border rounded-3 px-4" onClick={() => UploadedDocumentsModaltoggle(id, text)} variant='primary'>
                <span>{languageUse === 'English' ? 'View' : 'Voir'}</span>
            </Button>
        } else {
            return <>--</>
        }
    }

    /**
        * @function getStatus
        * @param status, id, text
        * @description use condition to return desired button.
    */
    const getStatus = (status, id, text) => {
        if (!status) {
            return <Button className="statusComplete login-btn-min-width border rounded-pill" onClick={() => handleEdit(status, id, text)} variant='outline-primary'>
                <span>{languageUse === 'English' ? 'Completed' : 'Terminé'}</span>
            </Button>
        } else {
            return <Button className="statusPending login-btn-min-width border rounded-pill" variant='outline-primary' onClick={() => handleEdit(status, id, text)}>
                <span>{languageUse === 'English' ? 'Pending' : 'En attente'}</span>
            </Button>
        }
    }

    /**
        * @function getStatusIcon
        * @param status, id
        * @description use condition to return desired icon.
    */
    const getStatusIcon = (status, id) => {
        if (!status) {
            return <div className="d-flex flex-row align-items-center">
                <AiOutlineEye style={{ fontSize: "20px" }} cursor={'pointer'} onClick={() => handleEdit(status, id)}></AiOutlineEye>
                <RxDashboard style={{ fontSize: "16px", marginLeft: '2rem' }} cursor={'pointer'} onClick={() => goToDashboard(id)}></RxDashboard>
            </div>
        } else {
            return <BsSkipEndCircle style={{ fontSize: "20px" }} cursor={'pointer'} onClick={() => handleEdit(status, id)} />
        }
    }

    /**
        * @function getViewIcon
        * @param status, id
        * @description use condition to return desired icon.
    */
    const getViewIcon = (status, id, text) => {
        if (!status) {
            return <div className="d-flex justify-content-center align-items-center">
                <AiOutlineEye role='button' size={20} onClick={() => handleEdit(status, id, text)} />
            </div>
        } else {
            return <BsSkipEndCircle style={{ fontSize: "20px" }} cursor={'pointer'} onClick={() => handleEdit(status, id, text)} />
        }
    }

    /**
        * @function getReportIcon
        * @param status, id
        * @description use condition to return desired icon.
    */
    const getReportIcon = (status, id) => {
        if (!status) {
            return <div className="d-flex flex-row align-items-center">
                <RxDashboard style={{ fontSize: "16px", marginLeft: '2rem' }} cursor={'pointer'} onClick={() => goToDashboard(id)}></RxDashboard>
            </div>
        }
    }

    /**
        * @function getReportProgressIcon
        * @param status, id
        * @description use condition to return desired icon.
    */
    const getReportProgressIcon = (status, id) => {
        if (!status) {
            return <div className="d-flex flex-row align-items-center">
                <RxDashboard style={{ fontSize: "16px", marginLeft: '2rem' }} cursor={'pointer'} onClick={() => goToProgressDashboard(id)}></RxDashboard>
            </div>
        }
    }

    /**
        * @function handleAssessmentPagination
        * @param page
        * @description Function to handle Pagination For Assessment
    */
    const handleAssessmentPagination = page => {
        setCurrentPage(page.selected)
        setoffsetCal(page.selected * 10)
    }

    /**
        * @function handleProgressAssessmentPagination
        * @param page
        * @description Function to handle Pagination For Progress Assessment
    */
    const handleProgressAssessmentPagination = page => {
        setProgressCurrentPage(page.selected)
        setProgressOffsetCal(page.selected * 10)
    }

    /**
        * @function getAssesmentList
        * @param
        * @description get Assesment List
    */
    const getAssesmentList = async () => {
        try {
            setAssesmentLoading(true)
            const result = await getAssesmentListService({
                authToken,
                limit: pageLimit,
                offset: offsetCal
            });
            setTotalEntry(result?.data?.data?.count)
            setAssesmentList(result?.data?.data?.list)
            setAssesmentLoading(false)
        } catch (error) {
            setAssesmentLoading(false)
            setAssesmentList([])
            toast.error(error?.response?.data?.message || 'Something went wrong')
        }
    }

    useEffect(() => {
        getAssesmentList()
    }, [offsetCal])

    /**
        * @function getProgressAssesmentList
        * @param
        * @description get Progress Assesment List
    */
    const getProgressAssesmentList = async () => {
        try {
            setAssesmentLoading(true)
            const result = await getProgressAssesmentListService({
                authToken,
                limit: pageLimit,
                offset: progressOffsetCal
            });
            setTotalProgressEntry(result?.data?.data?.count)
            setProgressAssesmentList(result?.data?.data?.list)
            setAssesmentLoading(false)
        } catch (error) {
            setAssesmentLoading(false)
            setProgressAssesmentList([])
            toast.error(error?.response?.data?.message || 'Something went wrong')
        }
    }

    useEffect(() => {
        getProgressAssesmentList()
    }, [progressOffsetCal])

    /**
        * @function startAssesment
        * @param ()
        * @description handle API to start the assesment
    */
    const startAssesment = async () => {
        try {
            const result = await startSubmissionService({
                authToken
            });
            const S_id = result?.data?.data?._id
            localStorage.setItem('submissionId', S_id);

            if (result?.data?.statusCode === 200) {
                const encryptedSubId = CryptoJS.AES.encrypt(S_id, 'mySecretKey').toString();
                navigate(`/${encodeURIComponent(encryptedSubId)}`)
                setLoading(false)
                dispatch(submissionId(S_id));
            } else {
                toast.success(result?.message)
                setLoading(false)
            }
        } catch (error) {
            setLoading(false);
            toast.error(error?.response?.data?.message || 'Something went wrong')
        }
    }

    /**
        * @function startProgressAssesment
        * @param ()
        * @description handle API to start the progress assesment
    */
    const startProgressAssesment = async () => {
        try {
            const result = await startProgressSubmissionService({
                authToken
            });
            const S_id = result?.data?.data?._id

            if (result?.data?.statusCode === 200) {
                const encryptedSubId = CryptoJS.AES.encrypt(S_id, 'mySecretKey').toString();
                navigate(`/progress/${encodeURIComponent(encryptedSubId)}`)
                setLoading(false)
                dispatch(submissionId(S_id));
            } else {
                toast.success(result?.message)
                setLoading(false)
            }
        } catch (error) {
            setLoading(false);
            toast.error(error?.response?.data?.message || 'Something went wrong')
        }
    }

    const handleAssessmentClick = (assessment) => {
        if (assessment.isDraft) {
            handleEdit(true, assessment._id, selectedType === 'regular' ? 'assessment' : 'progressAssessment');
        } else {
            handleEdit(false, assessment._id, selectedType === 'regular' ? 'assessment' : 'progressAssessment');
        }
    };

    useEffect(() => {
        // Update displayed assessments based on selected type
        if (selectedType === 'regular') {
            getAssesmentList();
        } else {
            getProgressAssesmentList();
        }
    }, [selectedType]);

    // Update the assessment status check to use actual data
    useEffect(() => {
        const checkAssessmentStatus = async () => {
            // Check if there are any completed regular assessments
            const hasCompletedRegular = assesmentList?.some(assessment => !assessment.isDraft);
            
            // Check if there are any completed progress assessments
            const hasCompletedProgress = progressAssesmentList?.some(assessment => !assessment.isDraft);
            
            setAssessmentStatus({
                regular: hasCompletedRegular,
                progress: hasCompletedProgress
            });
        };
        
        // Check status whenever assessment lists change
        if (assesmentList || progressAssesmentList) {
        checkAssessmentStatus();
        }
    }, [assesmentList, progressAssesmentList]);

    // User Journey State Management
    useEffect(() => {
        const currentUserState = journeyManager.determineUserState(assesmentList, progressAssesmentList);
        setUserJourneyState(currentUserState);
        
        // Check if we should show the user journey modal
        const shouldShowModal = journeyManager.shouldShowOnboarding(currentUserState);
        setShowUserJourneyModal(shouldShowModal);
        
        // Legacy welcome modal logic (keep for backward compatibility)
        const isNewUser = !assesmentList?.length && !progressAssesmentList?.length;
        // Remove showWelcomeModal state
    }, [assesmentList, progressAssesmentList]);

    // Handle URL parameters for actions
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const action = searchParams.get('action');
        const journey = searchParams.get('journey');
        
        if (action === 'start_readiness') {
            startAssesment();
        } else if (action === 'start_progress') {
            startProgressAssesment();
        } else if (journey && !['resume', 'progress_active'].includes(journey)) {
            // Show appropriate modal based on journey parameter, but not for resume or progress_active
            setShowUserJourneyModal(true);
        }
    }, [window.location.search]);

    const handleInfoClick = (type) => {
        const content = type === 'regular' ? {
            title: languageUse === 'English' ? 'Readiness Assessment Information' : 'Informations sur l\'évaluation de préparation',
            body: languageUse === 'English' 
                ? 'The Readiness Assessment is your first step in the ESG journey. It establishes your baseline performance across all ESG indicators. This comprehensive assessment must be completed before proceeding to the Progress Assessment.'
                : 'L\'évaluation de préparation est votre première étape dans le parcours ESG. Elle établit votre performance de base pour tous les indicateurs ESG. Cette évaluation complète doit être terminée avant de passer à l\'évaluation des progrès.'
        } : {
            title: languageUse === 'English' ? 'Progress Assessment Information' : 'Informations sur l\'évaluation des progrès',
            body: languageUse === 'English'
                ? 'The Progress Assessment helps track your ESG improvements over time. It becomes available only after completing the Readiness Assessment, allowing you to measure and document your advancement in ESG practices.'
                : 'L\'évaluation des progrès vous aide à suivre vos améliorations ESG au fil du temps. Elle devient disponible uniquement après avoir terminé l\'évaluation de préparation, vous permettant de mesurer et de documenter votre progression dans les pratiques ESG.'
        };
        setModalContent(content);
        setShowInfoModal(true);
    };

    // Remove WelcomeModal component
    // Remove WelcomeModal component definition
    // Remove all setShowWelcomeModal and showWelcomeModal logic
    // Remove <WelcomeModal /> from JSX

    return (
        <>
            <NavbarComponent />
            <div>
                <Toaster />
            </div>
            {assesmentLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                    <Spinner animation="grow" />
                </div>
            ) : (
                <StyledHistory>
                    <div className="container-fluid">
                        <div className="header-image">
                            <img 
                                src={AssessmentHeaderImage}
                                alt="Assessment Type Header" 
                            />
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h2 className="mb-0">Welcome! What would you like to do?</h2>
                            <Button
                                variant="outline-primary"
                                className="d-flex align-items-center"
                                onClick={() => navigate('/knowledge-base')}
                            >
                                <i className="bi bi-journal-bookmark me-2"></i>
                                Knowledge Base
                            </Button>
                        </div>
                        <div className="selection-section">
                                {/* Assessment Type Selection and Information */}
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <div className="d-flex align-items-center">
                                        <h4 className="mb-0 me-3">
                                            {languageUse === 'English' ? 'Assessment Type' : 'Type d\'évaluation'}
                                        </h4>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant={selectedType === 'regular' ? 'primary' : 'outline-primary'}
                                                onClick={() => setSelectedType('regular')}
                                            >
                                                {languageUse === 'English' ? 'Readiness Assessment' : 'Évaluation de préparation'}
                                            </Button>
                                            <Button
                                                variant={selectedType === 'progress' ? 'primary' : 'outline-primary'}
                                                onClick={() => setSelectedType('progress')}
                                            >
                                                {languageUse === 'English' ? 'Progress' : 'Progrès'}
                                            </Button>
                                        </div>
                                    </div>
                                    <Button variant="outline-info" onClick={() => handleInfoClick(selectedType)}>
                                        <i className="bi bi-info-circle me-2"></i>
                                        {languageUse === 'English' ? 'Information' : 'Information'}
                                    </Button>
                                </div>

                                {/* Assessment Selection and List */}
                                <div className="selection-section">
                                    <h2>{languageUse === 'English' ? "Select Assessment Type" : "Sélectionner le type d'évaluation"}</h2>
                                    <div className="assessment-types">
                                        <div className={`assessment-type ${selectedType === 'regular' ? 'active' : ''}`} onClick={() => setSelectedType('regular')}>
                                            <i className="bi bi-clipboard-check"></i>
                                            {languageUse === 'English' ? "Readiness Assessment" : "Évaluation de préparation"}
                                        </div>
                                        <div className={`assessment-type ${selectedType === 'progress' ? 'active' : ''}`} onClick={() => setSelectedType('progress')}>
                                            <i className="bi bi-graph-up"></i>
                                            {languageUse === 'English' ? "Progress Assessment" : "Évaluation des progrès"}
                                        </div>
                                    </div>
                                </div>

                                <div className="assessment-cards">
                                    {(selectedType === 'regular' ? assesmentList : progressAssesmentList)?.map((assessment, index) => (
                                        <div key={index} className="assessment-card" onClick={() => handleAssessmentClick(assessment)}>
                                            <div className="card-header">
                                                <i className={`bi ${selectedType === 'regular' ? 'bi-clipboard-check' : 'bi-graph-up'}`}></i>
                                                <h3>{assessment.title || (languageUse === 'English' ? 
                                                    `Readiness Assessment ${index + 1}` : 
                                                    `${selectedType === 'regular' ? 'Évaluation de préparation' : 'Évaluation des progrès'} ${index + 1}`)}</h3>
                                            </div>
                                            <div className="card-content">
                                                <p>{assessment.description || (languageUse === 'English' ? 
                                                    `Initial ${selectedType === 'regular' ? 'Readiness' : 'Progress'} assessment to evaluate your organization's ESG performance and establish baseline metrics` : 
                                                    `Évaluation initiale ${selectedType === 'regular' ? 'de préparation' : 'des progrès'} pour évaluer la performance ESG de votre organisation et établir des mesures de référence`)}</p>
                                            </div>
                                            <div className="card-footer">
                                                <span className="date">{moment(assessment?.updatedAt).format("DD-MM-YYYY")}</span>
                                                <span className={`status ${assessment.isDraft ? 'in-progress' : 'completed'}`}>
                                                    {assessment.isDraft ? 
                                                        (languageUse === 'English' ? 'In Progress' : 'En cours') : 
                                                        (languageUse === 'English' ? 'Completed' : 'Terminé')}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {(selectedType === 'regular' ? assesmentList : progressAssesmentList)?.length === 0 && (
                                        <div className='pt-5 ps-0 text-center w-100'>
                                            <div className='w-100 rounded bgColor-noQ height-noQ d-flex align-items-center justify-content-center'>
                                                <p>{languageUse === 'English' ? 
                                                    `Please start the ${selectedType} assessment.` : 
                                                    `Veuillez commencer l'évaluation ${selectedType === 'regular' ? 'de préparation' : 'des progrès'}`}</p>
                                            </div>
                                            <Button 
                                                variant={selectedType === 'regular' ? 'primary' : 'success'} 
                                                onClick={selectedType === 'regular' ? startAssesment : startProgressAssesment}
                                                className="mt-4"
                                            >
                                                {isLoading ? <Spinner size="sm" className="me-2" /> : <i className="bi bi-plus-lg me-2"></i>}
                                                {languageUse === 'English' ? 'Start New' : 'Commencer nouveau'}
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {/* Reports Section */}
                                <div className="report-section">
                                    <h4 className="mb-4">
                                        <i className="bi bi-file-earmark-text me-2"></i>
                                        {languageUse === 'English' ? 'Assessment Reports' : 'Rapports d\'évaluation'}
                                    </h4>
                                    <Row>
                                        <Col md={6} className="mb-4">
                                            <div className={`report-card ${!assessmentStatus.regular ? 'disabled' : ''}`}>
                                                <h5>
                                                    <i className="bi bi-clipboard-data me-2"></i>
                                                    {languageUse === 'English' ? 'Readiness Assessment Report' : 'Rapport d\'évaluation de préparation'}
                                                </h5>
                                                <p className="text-muted">
                                                    {languageUse === 'English'
                                                        ? 'View your baseline ESG performance report and detailed analysis.'
                                                        : 'Consultez votre rapport de performance ESG de base et l\'analyse détaillée.'}
                                                </p>
                                                <Button 
                                                    variant="outline-primary" 
                                                    disabled={!assessmentStatus.regular}
                                                    className="w-100"
                                                    onClick={() => assessmentStatus.regular && goToDashboard(assesmentList[0]?._id)}
                                                >
                                                    <i className="bi bi-download me-2"></i>
                                                    {languageUse === 'English' ? 'Download Report' : 'Télécharger le rapport'}
                                                </Button>
                                            </div>
                                        </Col>
                                        <Col md={6} className="mb-4">
                                            <div className={`report-card ${!assessmentStatus.progress ? 'disabled' : ''}`}>
                                                <h5>
                                                    <i className="bi bi-graph-up-arrow me-2"></i>
                                                    {languageUse === 'English' ? 'Progress Assessment Report' : 'Rapport d\'évaluation des progrès'}
                                                </h5>
                                                <p className="text-muted">
                                                    {languageUse === 'English'
                                                        ? 'Track your ESG improvement journey with comparative analysis.'
                                                        : 'Suivez votre parcours d\'amélioration ESG avec une analyse comparative.'}
                                                </p>
                                                <Button 
                                                    variant="outline-primary" 
                                                    disabled={!assessmentStatus.progress}
                                                    className="w-100"
                                                    onClick={() => assessmentStatus.progress && goToProgressDashboard(progressAssesmentList[0]?._id)}
                                                >
                                                    <i className="bi bi-download me-2"></i>
                                                    {languageUse === 'English' ? 'Download Report' : 'Télécharger le rapport'}
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                            </div>
                        </div>
                    </div>
                </StyledHistory>
            )}

            <Footer />
            <UploadedDocumentsModal modal={uploadedDocumentsModal} toggle={UploadedDocumentsModaltoggle} handleShowPdfModalShow={handleShowPdfModalShow} submissionId={submissionId} setPdfItem={setPdfItem} progress={progress} />
            <ShowPdfModal show={showPdfModalShow} handleClose={handleShowPdfModalShow} pdfItem={pdfItem} />
            
            {/* Enhanced User Journey Modal */}
            <UserJourneyModal
                show={showUserJourneyModal}
                onHide={() => setShowUserJourneyModal(false)}
                userState={userJourneyState}
                readinessAssessments={assesmentList}
                progressAssessments={progressAssesmentList}
                languageUse={languageUse}
                onStartReadiness={startAssesment}
                onStartProgress={startProgressAssesment}
            />

            <Modal show={showInfoModal} onHide={() => setShowInfoModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{modalContent.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{modalContent.body}</p>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default History;
